import type { UIViewInjectedProps } from '@uirouter/react';
import { observer } from 'mobx-react-lite';
import moment from 'moment';

import { api } from '@/api';
import { getAllowedFormFields } from '@/components/generic-form/utils';
import { DrawerFormWithNote } from '@/components/note-form';
import { PERMISSION_OBJ_TRANSIT_ORDER } from '@/constants/permissions';
import {
  addressStore,
  boardingStore,
  containerStore,
  counterpartyContractStore,
  counterpartyStore,
  productStore,
  transitOrderStore,
} from '@/stores';
import { NoteObjectType } from '@/types/enums';
import { getTranslatedString } from '@/utils';
import { withoutRepeatValue } from '@/utils/controller';
import { useMassUpdateCashedStores } from '@/utils/store';

import { getFormFields } from './setup';

const entity = 'transit-orders';
const store = transitOrderStore;

export const Form = observer(({ transition }: UIViewInjectedProps) => {
  const isCreate = transition.router.globals.current.name === `base-layout.${entity}.create`;
  const { transitOrderId } = transition.router.globals.params;
  const foundItem = store.items.find(({ id }: any) => id === +transitOrderId);

  const { loading } = useMassUpdateCashedStores([
    addressStore,
    boardingStore,
    counterpartyContractStore,
    counterpartyStore,
    productStore,
    containerStore,
  ]);

  const onClose = () => {
    transition.router.stateService.go(`base-layout.${entity}`);
  };

  const initValues =
    !isCreate && foundItem
      ? {
          ...foundItem,
          boardingId: foundItem.boarding?.id,
          freightForwarderId: foundItem.freightForwarder?.id,
          productId: foundItem.product?.id,
          containerId: foundItem.container?.id,
          departureAddressId: foundItem.departureAddress?.id,
          destinationAddressId: foundItem.destinationAddress?.id,
          exporterId: foundItem.exporter?.id,
          internalClientId: foundItem.internalClient?.id,
          finalClientId: foundItem.finalClient?.id,
          consigneeId: foundItem.consignee?.id,
          finalConsigneeId: foundItem.finalConsignee?.id,
          freightPayerId: foundItem.freightPayer?.id,
          internalClientContractId: foundItem.internalClientContract?.id,
          finalClientContractId: foundItem.finalClientContract?.id,
          serviceContractId: foundItem.serviceContract?.id,
          netWeight: foundItem.statistics.netWeight,
          etaDate: foundItem.etaDate && moment(foundItem.etaDate),
        }
      : {};

  const title = isCreate
    ? getTranslatedString('transit-orders.create-new')
    : getTranslatedString('transit-orders.transit-order-number', { 0: transitOrderId });

  const formFields = getFormFields();
  const allowedFields = getAllowedFormFields(formFields, isCreate, PERMISSION_OBJ_TRANSIT_ORDER);

  const resourceController = withoutRepeatValue(initValues, {
    create: (values: any) => api.transitOrders.create(values).source.then(({ data }: any) => store.addItem(data)),
    update: (values: any) =>
      api.transitOrders.update(foundItem.id, values).source.then(({ data }: any) => store.updateItem(data)),
  });

  const layout = {
    labelCol: { span: 11 },
    wrapperCol: { span: 13 },
  };

  return (
    <DrawerFormWithNote
      {...layout}
      objectType={NoteObjectType.TransitOrder}
      resourceId={transitOrderId}
      title={title}
      initValues={initValues}
      formFields={allowedFields}
      onClose={onClose}
      resourceController={resourceController}
      loaderCondition={!isCreate && !foundItem && loading}
    />
  );
});
